/**
 * Pure state rules for the Export Table advanced-filter editor.
 *
 * Kept out of the component so the row transitions — which operators a column
 * type offers, how many operands an operator takes, what survives a column or
 * operator change — are testable without rendering, and so the editor can never
 * persist a shape the backend's `validate_advanced_filters` would reject.
 *
 * The operator tables here are the frontend half of a contract whose other half
 * is `spotflow/spotflow/services/tables/advanced_filter.py`. Adding an operator
 * means adding it in both places, in the same change: an editor that offers an
 * operator the validator rejects is strictly worse than one that offers none.
 */

/** Column types the advanced editor can filter on. Mirrors
 * `FILTERABLE_COLUMN_TYPES`; `reference` is absent by design. */
export type FilterColumnType =
  | "string"
  | "number"
  | "boolean"
  | "datetime"
  | "file";

/** Persisted `op` identifiers. Never abbreviated — see the PRD's Operator Wire
 * Values table; these are a permanent contract. */
export type FilterOperator =
  | "equals"
  | "not_equal"
  | "contains"
  | "not_contains"
  | "greater_than"
  | "greater_than_or_equal"
  | "less_than"
  | "less_than_or_equal"
  | "after"
  | "on_or_after"
  | "before"
  | "on_or_before"
  | "between"
  | "in"
  | "not_in"
  | "is_empty"
  | "is_not_empty";

export interface FilterColumnMeta {
  name: string;
  label?: string | null;
  type: string;
}

/** One editor row. `field: ""` means no column picked yet — a blank draft row,
 * which is ignored on save and by the validator. */
export interface TableFilterRow {
  field: string;
  op: FilterOperator;
  value?: unknown;
  value2?: unknown;
}

/** One literal item in a membership list. Number columns carry real numbers so
 * the operand serializes as `10`, not `"10"` — the backend does not coerce. */
export type MembershipItem = string | number;

/** Mirrors `MAX_MEMBERSHIP_ITEMS` in `spotflow/spotflow/table_filters.py`. The
 * validator enforces it on resolved values; the editor stops the user from
 * building a list it would reject. */
export const MAX_MEMBERSHIP_ITEMS = 100;

/** Mirrors `MAX_ADVANCED_CONDITIONS` in `spotflow/spotflow/table_filters.py`. */
export const MAX_ADVANCED_CONDITIONS = 20;

/**
 * Whether another condition row may be added.
 *
 * Counts what the validator counts: blank drafts are not conditions (PRD 62),
 * so they take no room against the cap. Without this the editor happily saves a
 * 21st condition and the export is the first thing to complain, long after the
 * user has left the sidebar.
 */
export function canAddCondition(rows: TableFilterRow[]): boolean {
  const conditions = rows.filter((row) => row.field !== "").length;
  return conditions < MAX_ADVANCED_CONDITIONS;
}

const OPERATORS_BY_COLUMN_TYPE: Record<FilterColumnType, FilterOperator[]> = {
  string: [
    "equals",
    "not_equal",
    "contains",
    "not_contains",
    "in",
    "not_in",
    "is_empty",
    "is_not_empty",
  ],
  number: [
    "equals",
    "not_equal",
    "greater_than",
    "greater_than_or_equal",
    "less_than",
    "less_than_or_equal",
    "between",
    "in",
    "not_in",
    "is_empty",
    "is_not_empty",
  ],
  boolean: ["equals", "not_equal", "is_empty", "is_not_empty"],
  datetime: [
    "equals",
    "not_equal",
    "after",
    "on_or_after",
    "before",
    "on_or_before",
    "between",
    "is_empty",
    "is_not_empty",
  ],
  file: ["equals", "not_equal", "is_empty", "is_not_empty"],
};

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: "equals",
  not_equal: "not equal",
  contains: "contains",
  not_contains: "not contains",
  greater_than: ">",
  greater_than_or_equal: "≥",
  less_than: "<",
  less_than_or_equal: "≤",
  after: "after",
  on_or_after: "on or after",
  before: "before",
  on_or_before: "on or before",
  between: "between",
  in: "is any of",
  not_in: "is none of",
  is_empty: "empty",
  is_not_empty: "not empty",
};

/** How many operands the operator requires: 0, 1 (`value`) or 2 (+ `value2`). */
const OPERATOR_ARITY: Record<FilterOperator, 0 | 1 | 2> = {
  equals: 1,
  not_equal: 1,
  contains: 1,
  not_contains: 1,
  greater_than: 1,
  greater_than_or_equal: 1,
  less_than: 1,
  less_than_or_equal: 1,
  after: 1,
  on_or_after: 1,
  before: 1,
  on_or_before: 1,
  between: 2,
  in: 1,
  not_in: 1,
  is_empty: 0,
  is_not_empty: 0,
};

export function isFilterColumnType(type: string): type is FilterColumnType {
  return type in OPERATORS_BY_COLUMN_TYPE;
}

export function operatorsForColumnType(type: string): FilterOperator[] {
  return isFilterColumnType(type) ? OPERATORS_BY_COLUMN_TYPE[type] : [];
}

export function isFilterOperator(op: string): op is FilterOperator {
  return op in OPERATOR_LABELS;
}

export function operatorLabel(op: FilterOperator): string {
  return OPERATOR_LABELS[op];
}

export function operandCount(op: FilterOperator): 0 | 1 | 2 {
  return OPERATOR_ARITY[op];
}

export function columnType(
  columns: FilterColumnMeta[],
  field: string
): string | undefined {
  return columns.find((c) => c.name === field)?.type;
}

/** A row with no column picked. Rendered, but not a condition until a column
 * is chosen (PRD 17, 59). */
export function blankRow(): TableFilterRow {
  return { field: "", op: "equals" };
}

function defaultOperatorFor(type: string): FilterOperator {
  return operatorsForColumnType(type)[0] ?? "equals";
}

/**
 * Picking a different column resets the operator and clears both operands
 * (PRD 13). The previous operator may not exist for the new type, and an
 * operand typed for the old column is meaningless against the new one — the
 * backend would reject it, so the editor must not carry it over.
 */
export function withColumn(
  row: TableFilterRow,
  field: string,
  columns: FilterColumnMeta[]
): TableFilterRow {
  if (row.field === field) return row;
  return { field, op: defaultOperatorFor(columnType(columns, field) ?? "") };
}

/**
 * The literal type an operand control must produce for a column, so a Number
 * operand serializes as `10` and not `"10"`. PRD 22 forbids coercion, so a
 * stringifying editor would build a node that always errors at run time.
 */
export function operandDataType(
  type: string
): "number" | "bool" | "datetime" | "File" | "str" {
  if (type === "number") return "number";
  if (type === "boolean") return "bool";
  if (type === "datetime") return "datetime";
  if (type === "file") return "File";
  return "str";
}

/** A whole-field `:var[...]` token. Such an operand stays a string through the
 * editor: it is resolved upstream at run time, and typed there. */
const WHOLE_REF = /^\s*:var\[[^\]]+\]\s*$/;

/**
 * Whether an operand is bound to an upstream variable rather than typed.
 *
 * A bound operand is opaque to the editor: only the run knows its value, and
 * only the backend's `validate_advanced_filters` can type-check it. So the
 * editor's job is to leave it alone — in particular a bound *membership*
 * operand is a `:var[...]` string where a literal one is an array, and reading
 * it as "an empty chip list" would erase the binding on the next save.
 */
export function isBoundOperand(value: unknown): boolean {
  return typeof value === "string" && WHOLE_REF.test(value);
}

/** Typed operand → the string the literal control edits. */
export function operandToInput(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

/**
 * The string the literal control produced → the typed operand to persist.
 *
 * Text with no finite numeric value is passed through unchanged rather than
 * becoming `NaN` or `Infinity`: the backend's "expects a number" names the
 * column, whereas either would surface as an opaque driver error.
 */
function asNumberOrKeepText(text: string): string | number {
  // `Number(" ")` and `Number("")` are both 0, which would invent a value the
  // user never typed — so blank and unparseable text is passed through and the
  // backend's "expects a number" names the column.
  //
  // Membership items come through here too, where a blank is a rejected item
  // rather than an absent operand — so the blank-is-no-value rule belongs to
  // `operandFromInput`, not here.
  if (text.trim() === "") return text;
  const parsed = Number(text);
  // Finite, not merely non-NaN: `Number("1e309")` is Infinity, which
  // `JSON.stringify` writes as `null`, so the persisted operand would stop
  // matching the box the user is looking at.
  return Number.isFinite(parsed) ? parsed : text;
}

export function operandFromInput(text: string, type: string): unknown {
  if (WHOLE_REF.test(text)) return text;
  // PRD 66: a File operand is a URI or nothing. Persisting '' would be a
  // filter for "the file whose URI is the empty string", which matches nothing
  // and reads as a data bug rather than a cleared picker.
  if (type === "file" && text === "") return null;
  if (type === "number") {
    // An emptied box is no value at all, not an unparseable number: `null`
    // draws the backend's "needs a value", where passing the blank through
    // would draw "expects a number" for a control the user can see is empty.
    return text.trim() === "" ? null : asNumberOrKeepText(text);
  }
  if (type === "boolean") {
    if (text === "true") return true;
    if (text === "false") return false;
    return text;
  }
  return text;
}

export function isMembershipOperator(op: FilterOperator): boolean {
  return op === "in" || op === "not_in";
}

/** The persisted operand read back as a chip list. Anything else — a stale
 * scalar from a hand-edited config, an absent operand — reads as no items, so
 * the editor renders an empty chip box rather than crashing on it. */
export function membershipItems(value: unknown): MembershipItem[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is MembershipItem =>
      typeof item === "string" || typeof item === "number"
  );
}

/**
 * Chip-editor entry, for both Enter (one item) and paste (many).
 *
 * Newlines are the only separator (PRD 40, 41): a Text value may legitimately
 * contain a comma, so splitting on it would quietly turn "Smith, Ada" into two
 * wrong values. Items are not trimmed either — whitespace-only Text is a real
 * value (PRD 60) — but an empty line is dropped, because the backend rejects
 * `''` outright and a trailing newline in a paste would otherwise fail the
 * whole export.
 *
 * Duplicates are removed here as well as server-side (PRD 42) so the list the
 * user sees is the list they will get, and the cap stops entry rather than
 * letting the run fail later.
 */
export function addMembershipItems(
  current: MembershipItem[],
  text: string,
  type: string
): MembershipItem[] {
  const out = [...current];
  // Items within one column are all the same type, so their string form is a
  // safe identity: a literal "10" cannot coexist with 10 on a Number column.
  const seen = new Set(current.map(String));
  for (const line of text.split(/\r?\n/)) {
    if (line === "" || out.length >= MAX_MEMBERSHIP_ITEMS) continue;
    const item = type === "number" ? asNumberOrKeepText(line) : line;
    if (seen.has(String(item))) continue;
    seen.add(String(item));
    out.push(item);
  }
  return out;
}

/**
 * Changing the operator keeps the first operand only while its type stays
 * compatible (PRD 14).
 *
 * An operator taking no operand has nothing for the old one to be compatible
 * with, so the row is stripped. Keeping it would leave a value the editor no
 * longer renders but still holds, which reappears the moment the user picks an
 * operator that takes one again.
 *
 * Crossing the scalar/list boundary is never compatible either: a scalar left
 * under `in` — or a list left under `equals` — serializes into a condition the
 * backend rejects, so the export would fail on a config that saved without
 * complaint.
 *
 * The second operand belongs to `between` alone (PRD 15), so leaving it drops
 * the upper bound rather than parking it out of reach.
 */
export function withOperator(
  row: TableFilterRow,
  op: FilterOperator
): TableFilterRow {
  if (operandCount(op) === 0) return { field: row.field, op };
  if (isMembershipOperator(row.op) !== isMembershipOperator(op)) {
    return { field: row.field, op };
  }
  if (operandCount(op) === 2) return { ...row, op };
  const { value2: _upperBound, ...kept } = row;
  return { ...kept, op };
}

/**
 * The legacy equals map read as conditions, for the merged editor.
 *
 * Equality is one operator among many now, so a saved `filters` entry is just a
 * condition — but it has to be typed by its column on the way in, because the
 * legacy map stored everything as text and the validator does not coerce.
 *
 * Blank entries are dropped. The legacy path ignored blank *text* outright, and
 * carrying one over as `equals: ''` would invent a condition the user never
 * wrote — `''` is a meaningful Text equality here (PRD 29). A blank on a Number
 * column did something worse in the legacy path: it compiled to
 * `CAST(col AS TEXT) = ''` and matched nothing, so an emptied box silently
 * exported zero rows. Dropping it makes the empty box mean what it looks like.
 *
 * Entries naming a column that no longer exists are dropped too, exactly as
 * `_filter_specs` already skips stale keys.
 */
export function legacyFiltersAsRows(
  filters: unknown,
  columns: FilterColumnMeta[]
): TableFilterRow[] {
  if (!filters || typeof filters !== "object" || Array.isArray(filters)) {
    return [];
  }
  const rows: TableFilterRow[] = [];
  for (const [field, raw] of Object.entries(filters)) {
    const type = columnType(columns, field);
    if (type === undefined || !isFilterColumnType(type)) continue;
    if (!holdsLegacyValue(raw)) continue;
    rows.push({
      field,
      op: "equals",
      value: operandFromInput(String(raw), type),
    });
  }
  return rows;
}

/** Whether a legacy entry holds a value `_filter_specs` turns into a filter.
 * Absent values and blank text are skipped there too. */
function holdsLegacyValue(raw: unknown): boolean {
  return raw !== null && raw !== undefined && String(raw).trim() !== "";
}

/**
 * The legacy equals entries the conditions list cannot take over, keyed as they
 * were stored.
 *
 * `_filter_specs` applies the legacy map against the *live* columns, so an entry
 * it honours keeps filtering the export whatever this editor knows. Three kinds
 * have nowhere to migrate to: every entry when there is no config-time schema to
 * type it against, the `id` pseudo-column (`_filter_specs` special-cases it and
 * `validate_advanced_filters` has no column for it), and a column whose type
 * carries no operators. Clearing those alongside the migrated ones would drop a
 * live filter and change what the export returns, so they stay put.
 *
 * Stale keys and blanks are not returned: `_filter_specs` ignores them too, so
 * letting the migration clear them loses nothing.
 */
export function retainedLegacyFilters(
  filters: unknown,
  columns: FilterColumnMeta[]
): Record<string, unknown> {
  if (!filters || typeof filters !== "object" || Array.isArray(filters)) {
    return {};
  }
  const out: Record<string, unknown> = {};
  for (const [field, raw] of Object.entries(filters)) {
    if (!holdsLegacyValue(raw)) continue;
    if (columns.length === 0 || field === "id") {
      out[field] = raw;
      continue;
    }
    const type = columnType(columns, field);
    if (type !== undefined && !isFilterColumnType(type)) out[field] = raw;
  }
  return out;
}

/**
 * Serialize rows for persistence: blank rows are dropped, and each row carries
 * only `field`, `op` and the operands its operator actually requires — never a
 * column type (derived from live schema instead), never an unused operand key.
 */
export function serializeRows(rows: TableFilterRow[]): TableFilterRow[] {
  return rows
    .filter((row) => row.field !== "")
    .map((row) => {
      const arity = operandCount(row.op);
      const out: TableFilterRow = { field: row.field, op: row.op };
      if (arity >= 1) {
        // A membership operand persists as the list the chip box is showing,
        // so an untouched one is `[]` and the backend answers "needs at least
        // one value" rather than the shape complaint a `null` would draw. A
        // stale scalar under `in` normalises the same way: the editor never
        // displayed it, and the condition still errors rather than passing.
        //
        // A *bound* membership operand is the exception: it is a `:var[...]`
        // string, not an array, and the list only exists at run time. Passing
        // it through `membershipItems` would flatten every binding to `[]` on
        // the first save after it was made.
        out.value =
          isMembershipOperator(row.op) && !isBoundOperand(row.value)
            ? membershipItems(row.value)
            : (row.value ?? null);
      }
      if (arity >= 2) out.value2 = row.value2 ?? null;
      return out;
    });
}
